import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Typography,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { agentService, Agent, CreateAgentData } from '../services/agents';
import { teamService, Team } from '../services/teams';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import '../styles/resizable-table.css';

const { Title } = Typography;

export const Agents: React.FC = () => {
  const listingKey = 'agents';
  const [agents, setAgents] = useState<Agent[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [form] = Form.useForm<CreateAgentData>();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [agentsResponse, teamsResponse] = await Promise.all([
        agentService.getAll(),
        teamService.getAll(),
      ]);
      setAgents(agentsResponse.data || []);
      setTeams(teamsResponse.data || []);
    } catch {
      message.error('Failed to load agents data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAgent(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true });
    setModalVisible(true);
  };

  const handleEdit = (agent: Agent) => {
    setEditingAgent(agent);
    form.setFieldsValue({
      ...agent,
      enabled: agent.enabled,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await agentService.delete(id);
      message.success('Agent deleted');
      fetchData();
    } catch {
      message.error('Failed to delete agent');
    }
  };

  const handleSubmit = async (values: CreateAgentData) => {
    const payload = {
      ...values,
      enabled: values.enabled ?? true,
    };

    try {
      if (editingAgent) {
        await agentService.update(editingAgent.id, payload);
        message.success('Agent updated');
      } else {
        await agentService.create(payload);
        message.success('Agent created');
      }
      setModalVisible(false);
      fetchData();
    } catch {
      message.error(editingAgent ? 'Failed to update agent' : 'Failed to create agent');
    }
  };

  const baseColumns = [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name', 
      width: 200,
      sorter: (a: Agent, b: Agent) => (a.name || '').localeCompare(b.name || ''),
    },
    { 
      title: 'Contact Number', 
      dataIndex: 'contact_number', 
      key: 'contact_number', 
      width: 180,
      sorter: (a: Agent, b: Agent) => (a.contact_number || '').localeCompare(b.contact_number || ''),
    },
    { 
      title: 'Email', 
      dataIndex: 'email', 
      key: 'email', 
      width: 250,
      sorter: (a: Agent, b: Agent) => (a.email || '').localeCompare(b.email || ''),
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      width: 160,
      sorter: (a: Agent, b: Agent) => (a.team?.name || '').localeCompare(b.team?.name || ''),
      render: (_: unknown, record: Agent) => record.team?.name || '-',
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      sorter: (a: Agent, b: Agent) => (a.enabled ? 1 : 0) - (b.enabled ? 1 : 0),
      render: (enabled: boolean) => (enabled ? 'Yes' : 'No'),
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue:
          col.dataIndex === 'name'
            ? (record: Agent) => record.name
            : col.dataIndex === 'contact_number'
            ? (record: Agent) => record.contact_number
            : col.dataIndex === 'email'
            ? (record: Agent) => record.email
            : col.dataIndex === 'team'
            ? (record: Agent) => record.team?.name
            : col.dataIndex === 'enabled'
            ? (record: Agent) => (record.enabled ? 'Yes' : 'No')
            : (undefined as unknown as (record: Agent) => string | number | null | undefined),
      })),
    [visibleColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Agent>(
    agents,
    memoizedColumns as any,
    memoizedColumns as any,
    { listingKey }
  );
  const tableColumns = useMemo(() => {
    const widths = columnsWithSearch.map((col) => {
      const rawWidth = col.width;
      if (typeof rawWidth === 'number') {
        return rawWidth;
      }
      if (typeof rawWidth === 'string') {
        const parsed = Number.parseFloat(rawWidth);
        return Number.isFinite(parsed) ? parsed : 150;
      }
      return 150;
    });
    const total = widths.reduce((sum, width) => sum + width, 0) || 1;
    return columnsWithSearch.map((col, index) => ({
      ...col,
      width: `${(widths[index] / total) * 100}%`,
    }));
  }, [columnsWithSearch]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Agents
          </Title>
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChange}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New Agent
            </Button>
          </Space>
        </div>

        <Table
          columns={tableColumns as any}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={false}
          footer={() => (
            <div style={{ textAlign: 'left', padding: '8px 0' }}>
              Total: {filteredData.length} {filteredData.length === 1 ? 'agent' : 'agents'}
            </div>
          )}
          onRow={(record) => ({
            onClick: () => handleEdit(record),
            style: { cursor: 'pointer' },
          })}
        />

      <Modal
        title={editingAgent ? 'Edit Agent' : 'New Agent'}
        open={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Agent Name"
            rules={[{ required: true, message: 'Please enter agent name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="contact_number" label="Contact Number">
            <Input />
          </Form.Item>

          <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Enter a valid email' }]}>
            <Input />
          </Form.Item>

          <Form.Item name="team_id" label="Team">
            <Select allowClear placeholder="Assign team">
              {teams.map((team) => (
                <Select.Option key={team.id} value={team.id}>
                  {team.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="enabled" label="Enabled" valuePropName="checked" initialValue>
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              {editingAgent && (
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this agent?')) {
                      handleDelete(editingAgent.id);
                      setModalVisible(false);
                    }
                  }}
                >
                  Delete
                </Button>
              )}
              <Button type="primary" htmlType="submit">
                {editingAgent ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
